<template>
  <div class="card-modern">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h3 class="card-title-modern">Aset Dengan Asset Number</h3>
        <small class="text-muted">
          Daftar Aset IT &amp; Infrastruktur IT yang sudah memiliki asset number (untuk inventaris &amp; audit).
        </small>
      </div>
    </div>
    <div class="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
      <button class="btn btn-sm bg-success-subtle border border-success-subtle text-success-emphasis"
        style="border-radius: 8px" :disabled="exporting" @click="doExport">
        <span v-if="exporting">
          <span class="spinner-border spinner-border-sm me-1"></span>Mengunduh...
        </span>
        <span v-else><i class="bi bi-file-earmark-excel me-1"></i>Export Excel</span>
      </button>
  
      <div style="width: 180px">
        <label class="form-label small text-muted mb-1">Tipe Sumber</label>
        <select v-model="filters.source_type" class="form-select" style="border-radius: 8px">
          <option value="">Semua</option>
          <option value="ASET_IT">Aset IT</option>
          <option value="INFRASTRUKTUR_IT">Infrastruktur IT</option>
        </select>
      </div>
  
      <div style="width: 140px">
        <label class="form-label small text-muted mb-1">Status</label>
        <select v-model="filters.status" class="form-select" style="border-radius: 8px">
          <option value="">Semua</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="expired">Expired</option>
        </select>
      </div>
  
      <div class="site-filter-ms" style="width: 220px; flex-shrink: 0">
        <label class="form-label small text-muted mb-1">Site / Cabang</label>
        <Multiselect v-model="filters.site_id" :options="sites" value-prop="mst_site_id" label="mst_site_name"
          :searchable="true" :can-clear="true" :close-on-select="true"
          no-options-text="Site tidak ditemukan" no-results-text="Site tidak ditemukan" />
      </div>
  
      <div style="width: 200px">
        <label class="form-label small text-muted mb-1">Cari</label>
        <input v-model="filters.search" class="form-control" style="border-radius: 8px" @keyup.enter="fetchData(1)" />
      </div>
  
      <button class="button-modern" @click="fetchData(1)">
        <i class="bi bi-search me-1"></i>Cari
      </button>
      <button class="btn btn-sm bg-light border text-dark" style="border-radius: 8px; height: 38px" @click="resetFilter">
        Reset
      </button>
    </div>
  
    <div v-if="!loading" class="mb-3">
      <span class="badge-modern badge-warning">
        {{ pagination.total }} item punya asset number
      </span>
    </div>
  
    <div class="modern-table-wrapper">
      <table class="table-modern">
        <thead>
          <tr>
            <th>No</th>
            <th>Sumber</th>
            <th>No Asset</th>
            <th>Device Type</th>
            <th>Merk / Tipe</th>
            <th>User / Lokasi</th>
            <th>Departemen</th>
            <th>Site</th>
            <th>Status</th>
            <!-- <th>Kondisi</th>
            <th>Tgl Beli</th>
            <th>Ganda</th> -->
            <th class="text-center">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="13" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>Memuat...
            </td>
          </tr>
          <tr v-else-if="!data.length">
            <td colspan="13" class="text-center text-muted py-4">Tidak ada data</td>
          </tr>
          <tr v-for="(item, i) in data" :key="`${item.source_type}-${item.number_id}`"
            :class="{ 'table-warning': item.is_duplicate }">
            <td>{{ rowNum(i) }}</td>
            <td>{{ sourceLabel(item.source_type) }}</td>
            <td>{{ item.asset_number || "-" }}</td>
            <td>{{ item.device_type_name || "-" }}</td>
            <td>{{ [item.merk, item.tipe].filter(Boolean).join(" / ") || "-" }}</td>
            <td>{{ item.user_or_location || "-" }}</td>
            <td>{{ item.division_name || "-" }}</td>
            <td>{{ item.site_name || "-" }}</td>
            <td>
              <span class="badge" :class="{
                        'bg-success': item.asset_status === 'active',
                        'bg-secondary': item.asset_status === 'inactive',
                        'bg-warning text-dark': item.asset_status === 'expired',
                      }">
                {{ item.asset_status || "-" }}
              </span>
            </td>
            <!-- <td>{{ item.kondisi || "-" }}</td>
            <td>{{ formatDate(item.purchase_date) }}</td>
            <td>
              <span v-if="item.is_duplicate" class="badge bg-danger">YA</span>
              <span v-else class="text-muted">-</span>
            </td> -->
            <td class="text-center">
              <button class="btn btn-sm btn-outline-primary" @click="openDetail(item)">
                <i class="bi bi-eye"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  
    <div v-if="pagination.total > pagination.limit" class="d-flex justify-content-between align-items-center mt-3">
      <small class="text-muted">
        Halaman {{ pagination.page }} dari {{ totalPages }}
      </small>
      <nav>
        <ul class="pagination pagination-sm mb-0">
          <li class="page-item" :class="{ disabled: pagination.page <= 1 }">
            <a class="page-link" href="#" @click.prevent="fetchData(pagination.page - 1)">Prev</a>
          </li>
          <li class="page-item" :class="{ disabled: pagination.page >= totalPages }">
            <a class="page-link" href="#" @click.prevent="fetchData(pagination.page + 1)">Next</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";
import { useRouter } from "vue-router";
import reportService from "@/services/reportAssetWithNumberService";
import siteService from "@/services/siteService";

const router = useRouter();
const data = ref([]);
const sites = ref([]);
const loading = ref(false);
const exporting = ref(false);
const filters = reactive({
  search: "",
  source_type: "",
  status: "",
  site_id: null,
});
const pagination = reactive({ page: 1, limit: 10, total: 0 });
const totalPages = computed(() =>
  Math.max(1, Math.ceil(pagination.total / pagination.limit))
);

// const formatDate = (v) => {
//   if (!v) return "-";
//   const d = new Date(v);
//   return isNaN(d) ? "-" : d.toISOString().slice(0, 10);
// };

const buildParams = (extra = {}) => {
  const p = { page: pagination.page, limit: pagination.limit, ...extra };
  if (filters.search) p.search = filters.search;
  if (filters.source_type) p.source_type = filters.source_type;
  if (filters.status) p.status = filters.status;
  if (filters.site_id) p.site_id = filters.site_id;
  return p;
};

const fetchData = async (page = 1) => {
  if (page < 1) return;
  loading.value = true;
  pagination.page = page;
  try {
    const res = await reportService.getList(buildParams({ page }));
    data.value = res.data.data || [];
    pagination.total = res.data.pagination?.total || 0;
  } catch (e) {
    console.error(e);
    alert("Gagal memuat data aset dengan number");
    data.value = [];
  } finally {
    loading.value = false;
  }
};

const resetFilter = () => {
  filters.search = "";
  filters.source_type = "";
  filters.status = "";
  filters.site_id = null;
  fetchData(1);
};

const doExport = async () => {
  exporting.value = true;
  try {
    const res = await reportService.exportExcel(buildParams({ page: 1, limit: 0 }));
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "Asset_With_Number.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert("Gagal export");
  } finally {
    exporting.value = false;
  }
};

const rowNum = (i) => (pagination.page - 1) * pagination.limit + i + 1;
const sourceLabel = (type) =>
  type === "INFRASTRUKTUR_IT" ? "Infrastruktur IT" : "Aset IT";

const openDetail = (item) => {
  const from = router.currentRoute.value.fullPath;
  if (item.source_type === "INFRASTRUKTUR_IT") {
    const id = item.parent_id || item.reference_id;
    router.push({
      path: `/asset/infrastructure-it/detail/${id}`,
      query: { from },
    });
  } else {
    router.push({
      path: `/asset/asset-it/detail/${item.reference_id}`,
      query: { from },
    });
  }
};

const isActiveSite = (x) => {
  const val = x?.status ?? x?.is_active;
  return val === undefined || val === null ? true : Number(val) === 1;
};

const loadSites = async () => {
  try {
    const res = await siteService.getAll();
    const list = (res.data || [])
      .filter(isActiveSite)
      .sort((a, b) =>
        String(a.mst_site_name || "").localeCompare(
          String(b.mst_site_name || ""),
          "id",
          { sensitivity: "base" },
        ),
      );
    sites.value = list;
  } catch {
    sites.value = [];
  }
};

onMounted(() => {
  loadSites();
  fetchData(1);
});
</script>

<style scoped>
.site-filter-ms :deep(.multiselect) {
  min-height: 38px;
  font-size: 0.875rem;
}

.site-filter-ms :deep(.multiselect-input) {
  font-size: 0.875rem;
}

.site-filter-ms :deep(.multiselect-search) {
  font-size: 0.875rem;
}
</style>